/**
 * globalKeydownInstaller — owns THE single legal global keydown
 * listener for the app.
 *
 * Why this file exists
 * --------------------
 * This is the single legal global keydown listener — WS-GOV-1 rule 8
 * (no-global-keydown, lands in a follow-up alongside this slice's
 * migration sweep) exempts THIS file specifically. Any other src/
 * file that calls `window.addEventListener('keydown', …)` is a
 * violation. The CmdK palette (NAV-1, shipped 1ab3773) is the one
 * legacy holdover and will migrate to `useKeyboardScope` once rule 8
 * lands; it is the explicitly-grandfathered exemption.
 *
 * Lifecycle
 * ---------
 * Refcounted: the FIRST `useKeyboardScope` mount calls
 * `acquireGlobalKeydownListener(dispatcher)`, every subsequent mount
 * bumps the refcount, every unmount decrements. When the count
 * returns to zero the listener detaches. This keeps Vitest /
 * StrictMode / hot-reload from stacking listeners.
 *
 * The installer does NOT own the stack — it accepts a dispatcher
 * callback that resolves the topmost scope at call time. That keeps
 * the file dependency-free (no React, no module-singleton stack
 * import) and makes the install/uninstall mechanism unit-testable.
 */

/** Signature of the dispatcher the installer forwards events to.
 *  Returns true iff the event was consumed (caller may then call
 *  `preventDefault` on the original). */
export type GlobalKeydownDispatcher = (event: KeyboardEvent) => boolean;

type InstallerState = {
    refcount: number;
    handler: ((e: KeyboardEvent) => void) | null;
    dispatcher: GlobalKeydownDispatcher | null;
};

const state: InstallerState = {
    refcount: 0,
    handler: null,
    dispatcher: null,
};

/** Acquire a hold on the global keydown listener. Returns a release
 *  function the caller MUST invoke on teardown. Calling release more
 *  than once is a no-op. */
export function acquireGlobalKeydownListener(
    dispatcher: GlobalKeydownDispatcher,
): () => void {
    // The dispatcher reference is set on first acquire. Subsequent
    // acquires reuse it — the hook layer ensures all callers point
    // at the same module-singleton stack, so a single dispatcher
    // suffices. If the contract ever drifts (e.g. tests want a
    // bespoke dispatcher), reset() below clears the reference.
    if (state.dispatcher === null) {
        state.dispatcher = dispatcher;
    }

    if (state.refcount === 0) {
        const handler = (e: KeyboardEvent): void => {
            const d = state.dispatcher;
            if (d) d(e);
        };
        state.handler = handler;
        if (typeof window !== 'undefined') {
            window.addEventListener('keydown', handler);
        }
    }
    state.refcount += 1;

    let released = false;
    return function release(): void {
        if (released) return;
        released = true;
        state.refcount = Math.max(0, state.refcount - 1);
        if (state.refcount === 0) {
            if (state.handler && typeof window !== 'undefined') {
                window.removeEventListener('keydown', state.handler);
            }
            state.handler = null;
            state.dispatcher = null;
        }
    };
}

/** Test-only escape hatch — drops the installer back to zero refs
 *  and detaches the listener. Hooks should NEVER call this; only
 *  the test setup uses it to guarantee inter-test isolation. */
export function __resetGlobalKeydownInstallerForTests(): void {
    if (state.handler && typeof window !== 'undefined') {
        window.removeEventListener('keydown', state.handler);
    }
    state.refcount = 0;
    state.handler = null;
    state.dispatcher = null;
}

/** Test-only inspector — returns the current refcount. Hooks
 *  should NEVER call this. */
export function __getGlobalKeydownRefcountForTests(): number {
    return state.refcount;
}
